import React, { useState, useEffect } from 'react';
import TransactionTable from './TransactionTable';
import SilverPrice from './SilverPrice';
import { getCsrfHeader } from './csrf';

const baseDateRangeOptions = [
  { label: "Last Two Weeks", url: "/transactions/twoweeks" },
  { label: "Last Two Months", url: "/transactions/twomonths" },
  { label: "Year-to-Date", url: "/transactions/yeartodate" },
];

const downloadOptions = [
  { label: "Year-to-Date", url: "/transactions/year.xlsx" },
  { label: "All Transactions", url: "/transactions/all.xlsx" },
  { label: "Last Complete Week", url: "/transactions/week.xlsx" },
  { label: "Current Week", url: "/transactions/current_week.xlsx" },
];

function App() {

  const categoriesUrl = "/categories";
  const remindersUrl = "/reminders";
  const yearsUrl = "/transactions/years";

  // ✅ Set the initial state correctly
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [dateRangeOptions, setDateRangeOptions] = useState(baseDateRangeOptions);
  const [url, setUrl] = useState(baseDateRangeOptions[0].url); // 🔥 Start with the two-weeks URL
  const [downloadUrl, setDownloadUrl] = useState(downloadOptions[0].url); // Defaults to Year-to-Date

  function handleDateRangeChange(e) {
    setTransactions([]); // 🔥 Clears transactions before fetching
    setUrl(e.target.value);
  }

  function handleDownload() {
    window.location.href = downloadUrl;
  }

  // Fetch every prior calendar year that has transaction data, so the dropdown reaches back as
  // far as the account's actual history rather than a hardcoded "last year only" option.
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(yearsUrl);
        const years = await response.json();
        const yearOptions = years.map(year => ({ label: String(year), url: `/transactions/year/${year}` }));
        setDateRangeOptions([...baseDateRangeOptions, ...yearOptions]);
      } catch (error) {
        console.log("error", error);
      }
    };

    fetchYears();
  }, []);

  // ✅ Fetch transactions when URL changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const responses = await Promise.all([fetch(url), fetch(categoriesUrl)]);
        const txs = await responses[0].json();
        const cats = await responses[1].json();

        setTransactions(txs); // ✅ Correctly replaces transactions instead of appending
        setCategories(cats);
      } catch (error) {
        console.log("error", error);
      }
    };

    if (url) fetchData(); // ✅ Only fetch if there's a valid URL
  }, [url]); // ✅ Runs when `url` changes

  const fetchReminders = async () => {
    try {
      const response = await fetch(remindersUrl);
      const data = await response.json();
      setReminders(data);
    } catch (error) {
      console.log("error", error);
    }
  };

  // ✅ Fetch reminders once on load
  useEffect(() => {
    fetchReminders();
  }, []);

  return (
    <div className="container">
      <div className="top-bar">
        <div className="date-range-group">
          <span className="date-range-label">Show transactions for</span>
          <select value={url} onChange={handleDateRangeChange} className="date-range-select">
            {dateRangeOptions.map(option => (
              <option key={option.url} value={option.url}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="top-bar-right">
          <a href="/account/edit" className="top-link edit-account-link">Edit Account</a>
          <a href="/logout" className="logout-link" title="Log Out">
            <img src="/images/logout-icon.png" alt="Logout" className="logout-icon" />
          </a>
        </div>
      </div>

      {/* Header Section */}
      <div className="header-box">
        <div className="downloads">
          <button onClick={handleDownload} className="download-button">Download Transactions</button>
          <select value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)} className="download-select">
            {downloadOptions.map(option => (
              <option key={option.url} value={option.url}>{option.label}</option>
            ))}
          </select>
        </div>

		<SilverPrice />
      </div>


      {/* Upload Section */}
      <div className="upload-box">
        <UploadForm id="cbts-upload-form" label="Upload Encore Paystub" url="/upload/encore" onUploadSuccess={fetchReminders} />
		{/*
				        <UploadForm id="chard-snyder-upload-form" label="Upload Chard Snyder Statement" url="/upload/chardsnyder" onUploadSuccess={fetchReminders} />
		*/}
        <UploadForm id="fidelity-hsa-upload-form" label="Upload Fidelity HSA CSV" url="/upload/fidelityhsa" onUploadSuccess={fetchReminders} />
        <UploadForm id="pnc-hsa-upload-form" label="Upload PNC HSA Statement" url="/upload/pnchsa" onUploadSuccess={fetchReminders} />
        <UploadForm id="newrez-upload-form" label="Upload NewRez Mortgage Statement" url="/upload/newrez" onUploadSuccess={fetchReminders} />
        <UploadForm id="rocket-upload-form" label="Upload Rocket Mortgage Statement" url="/upload/rocket" onUploadSuccess={fetchReminders} />
        <UploadForm id="capital-one-statement-upload-form" label="Upload Capital One Banking Statement" url="/upload/capitalone" onUploadSuccess={fetchReminders} />
        <UploadForm id="citi-thankyou-upload-form" label="Upload Citi ThankYou Statement" url="/upload/citithankyou" onUploadSuccess={fetchReminders} />
      </div>

      {/* Reminders Section */}
      {reminders.length > 0 && (
        <div className="reminders-box">
          <ul>
            {reminders.map((reminder, index) => (
              <li key={index}>{reminder}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Transaction Table */}
      <TransactionTable transactions={transactions} categories={categories} />
    </div>
  );
}

function UploadForm({ id, label, url, onUploadSuccess }) {
  const [file, setFile] = useState(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("file", file);
    fetch(url, {
      method: "POST",
      headers: getCsrfHeader(),
      body: formData,
    })
      .then(async (response) => {
        if (response.ok) {
          document.getElementById(id).reset();
          setFile(null);
          alert("File upload successful!");
          if (onUploadSuccess) onUploadSuccess();
        } else {
          const text = await response.text().catch(() => "");
          alert("File upload failed! " + (text || response.statusText));
        }
      })
      .catch((error) => {
        alert("File upload failed! " + error);
      });
  };

  return (
    <form id={id} onSubmit={handleSubmit} className="upload-form">
      <label htmlFor="file">{label}</label>
      <input type="file" id="file" name="file" onChange={(e) => setFile(e.target.files[0])} />
      <button type="submit" disabled={!file}>Upload</button>
    </form>
  );
}

export default App;
